/**
 * 금번 점검 사진 섹션 (Desktop 전용)
 * - 섹션 헤더: 위험영역/그리기메모 토글, AI분석/카메라/사진추가/우수사례 버튼
 * - 점검완료 배너 (sticky)
 * - 사진 카드 리스트: 위험요인 + 우수사례
 */
import { useState, useEffect, useRef } from 'react';
import { Button } from '@safetyhub/ui';
import { PlusIcon } from '../../icons';
import { useStickyOffset } from '../../../hooks/useStickyOffset';
import { PhotoCardVertical, PhotoEmptyState, FIRST_PHOTO_BUTTON_CLASS } from '../shared';
import type { HazardEditState } from '../shared';
import { RiskConfigDropdown, shouldShowRiskConfigDropdown } from '../shared/RiskConfigDropdown';
import { PhotoDropdownMenu } from './DropdownMenu';
import { PortalTooltip } from './PortalTooltip';
import type {
  Photo,
  FindingEditHandlers,
  CompletionState,
} from '../layouts/inspection-detail.types';

/** 시안 `.chk-label` — 툴바 좌측 토글의 라벨. */
const CHECK_LABEL_CLASS =
  'inline-flex cursor-pointer select-none items-center gap-2 text-[13.5px] font-semibold tracking-[-0.005em] text-ink-700';
/** 시안 `.chk-label input` — 17px, brand accent. */
const CHECKBOX_CLASS = 'm-0 h-[17px] w-[17px] cursor-pointer rounded-[5px] accent-brand-600';
/**
 * 시안 `.btn-best` — 우수사례 추가. 옆의 '사진 추가'(`.btn-photo-add` = `Button` primary)와
 * 같은 골격(38px·radius 10·14px/700·hover brightness)에 emerald 그라데이션만 다르다.
 * 전역 `Button`의 success는 평면 emerald라 시안과 갈리는데, success는 결재·완료 액션이 쓰는
 * 공용 서열이라 여기 한 화면 때문에 바꿀 수 없다. 그래서 클래스를 이 파일이 소유한다.
 */
const BEST_PRACTICE_BUTTON_CLASS =
  'inline-flex h-[38px] items-center justify-center gap-[7px] rounded-[10px] bg-gradient-to-br from-emerald-600 to-emerald-500 px-4 text-[14px] font-bold tracking-[-0.005em] text-white shadow-[0_6px_14px_-6px_rgba(5,150,105,0.55)] transition-all duration-150 enabled:hover:brightness-[1.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50';

export interface InspectionPhotoSectionProps {
  /** 섹션 헤더 sticky top 위치 (탭 있으면 tabBarBottom, 없으면 sectionStickyTop) */
  stickyTop: number;
  /** 섹션 표시 여부 */
  visible: boolean;

  // === Data ===
  inspectionId: string;
  /** 전체 사진 배열 (뷰어에서 전체 사진 탐색용) */
  allPhotos: Photo[];
  hazardPhotos: Photo[];
  bestPracticePhotos: Photo[];
  /** 참여자 수 (2명 이상이면 사진 업로더 표시) */
  contributorCount: number;

  // === Overlay 토글 ===
  showHazardOverlay: boolean;
  onShowHazardOverlayChange: (value: boolean) => void;
  showAnnotationOverlay: boolean;
  onShowAnnotationOverlayChange: (value: boolean) => void;

  // === 권한 ===
  canEdit: boolean;
  canEditFinding: boolean;
  canEditHazard: boolean;
  isCompletionLocked: boolean;
  hasReport: boolean;
  /** 이후 차수 점검 존재 여부 — 발견사항 해결은 가능하지만 다른 수정은 차단 */
  isLockedByLaterInspection?: boolean;
  isLoading: boolean;

  // === AI 분석 ===
  enableAIAnalysis: boolean;
  setEnableAIAnalysis?: (enabled: boolean) => void;
  focusCategories?: string[];
  setFocusCategories?: (categories: string[]) => void;

  // === 사진 액션 ===
  onAddPhoto: () => void;
  onAddBestPracticePhoto?: () => void;
  onOpenCamera?: () => void;
  onPhotoReview: (photoId: string, reviewed: boolean) => void;
  onPhotoDelete: (photoId: string) => void;
  onPhotoReanalyze: (photoId: string) => void;
  /** 분석 범위 지정 모달의 대상을 지목한다(모달은 점검 상세 페이지가 하나만 들고 있다) */
  onPhotoReanalyzeWithRegion: (photoId: string) => void;
  onPhotoDownload: (photoId: string) => void;

  // === 점검완료 ===
  completion?: CompletionState;

  // === 발견사항 선택 ===
  findingSelectionEnabled: boolean;
  onToggleMeasureSelection?: (findingId: string, measure: string, excluded: boolean) => void;

  // === 사진 선택 ===
  selectedPhotoIds: Set<string>;
  onSelectionChange: (photoId: string, checked: boolean) => void;

  // === 권한 체크 함수 ===
  canReviewPhoto: (photo: Photo) => boolean;
  canModifyPhoto: (photo: Photo) => boolean;

  // === 발견사항 편집 ===
  findingEditHandlers?: FindingEditHandlers;
  highlightedFindingId: string | null;
  onHighlightedFindingIdChange: (id: string | null) => void;

  // === 위험영역 편집 ===
  hazardEdits: Map<string, HazardEditState>;
  onHazardChange?: (findingId: string, boundingBox: import('../../../lib/inspections').BoundingBox | null) => void;
  onHazardChangeEnd?: (findingId: string, boundingBox: import('../../../lib/inspections').BoundingBox) => void;
  onHazardDeleteSave?: (findingId: string) => void;
  onHazardRestore?: (findingId: string) => void;

  // === 위험요인 해소 ===
  hazardResolutionEnabled: boolean;
  onOpenHazardResolution?: (photoId: string, findingId?: string) => void;

  // === 사진 뷰어 ===
  openPhotoViewer: (photos: Photo[], index: number) => void;

  // === 발견사항 추가 ===
  hasAddFinding: boolean;
  onOpenAddFindingSheet: (photoId: string, mode?: 'hazard' | 'best-practice') => void;

  // === 피처 플래그 ===
  bestPracticeEnabled: boolean;

  // === Context ===
  companyId?: string;
  siteId?: string;

  // === 사진 메타 편집 ===
  onPhotoMetaUpdate?: (photoId: string, updates: {
    location?: { id: string; name: string } | null;
    detailLocation?: { id: string; name: string } | null;
    equipment?: { id: string; name: string; managementNumber?: string | null; processType?: string | null } | null;
    processType?: string | null;
  }) => void;

  // === 개선대책 해제 권한 판별용 ===
  /** 현재 로그인 사용자 ID (본인 선택 해제 권한 판별용) */
  currentUserId?: string;
  /** 현재 사용자가 점검 참여자 중 PRIMARY 역할인지 (override 권한) */
  isPrimary?: boolean;
  /** 현재 사용자가 조직 Owner인지 (override 권한) */
  isOwner?: boolean;

  // === ResizeObserver 재트리거용 ===
  activeInspectionTab?: string;

  // === Phase N3: 위험성평가 ===
  riskConfig?: import('@safetyhub/shared').RiskAssessmentConfig | null;

  // === Phase N3+: 점검별 config override ===
  inspectionId_?: string;
  riskAssessmentConfigId?: string | null;
  allRiskConfigs?: import('../../../types/risk-assessment-config').RiskAssessmentConfig[];
  onRiskConfigChange?: (configId: string | null) => Promise<void>;
  /**
   * 평가 방식을 바꿀 수 있는가 — 일반 편집 권한(canEdit)과 **별개**다.
   *
   * 점검이 완료되면 방식은 잠긴다(2026-08-06 사용자 확정): 완료 후 방식이 바뀌면 아직 값이
   * 박제되지 않은 보고서의 등급이 따라 움직인다. 미지정이면 canEdit을 따른다(종전 동작).
   * ⚠️ 드롭다운을 숨기지 말고 **읽기 전용으로** 둘 것 — 어떤 방식으로 평가했는지는 완료 후에도
   * 보여야 한다.
   */
  canEditRiskConfig?: boolean;
}

export function InspectionPhotoSection({
  stickyTop,
  visible,
  inspectionId,
  allPhotos,
  hazardPhotos,
  bestPracticePhotos,
  contributorCount,
  showHazardOverlay,
  onShowHazardOverlayChange,
  showAnnotationOverlay,
  onShowAnnotationOverlayChange,
  canEdit,
  canEditFinding,
  canEditHazard,
  isCompletionLocked,
  hasReport,
  isLockedByLaterInspection = false,
  isLoading,
  enableAIAnalysis: _enableAIAnalysis,
  setEnableAIAnalysis: _setEnableAIAnalysis,
  focusCategories = [],
  setFocusCategories: _setFocusCategories,
  onAddPhoto,
  onAddBestPracticePhoto,
  onOpenCamera,
  onPhotoReview,
  onPhotoDelete,
  onPhotoReanalyze,
  onPhotoReanalyzeWithRegion,
  onPhotoDownload,
  completion,
  findingSelectionEnabled,
  onToggleMeasureSelection,
  selectedPhotoIds,
  onSelectionChange,
  canReviewPhoto,
  canModifyPhoto,
  findingEditHandlers,
  highlightedFindingId,
  onHighlightedFindingIdChange,
  hazardEdits,
  onHazardChange,
  onHazardChangeEnd,
  onHazardDeleteSave,
  onHazardRestore,
  hazardResolutionEnabled,
  onOpenHazardResolution,
  openPhotoViewer,
  hasAddFinding,
  onOpenAddFindingSheet,
  bestPracticeEnabled,
  companyId,
  siteId,
  onPhotoMetaUpdate,
  activeInspectionTab,
  currentUserId,
  isPrimary = false,
  isOwner = false,
  riskConfig,
  riskAssessmentConfigId,
  allRiskConfigs = [],
  onRiskConfigChange,
  canEditRiskConfig,
}: InspectionPhotoSectionProps) {
  // 평가 방식 편집 가능 여부의 유효값 — 잠금 축이 canEdit과 분리돼 있으므로 표시 게이트도 이 값을 본다.
  const canEditRiskConfigEffective = canEditRiskConfig ?? canEdit;

  // 섹션 헤더 하단 위치 → 점검완료 배너 sticky top
  const { ref: sectionHeaderRef, stickyTop: sectionHeaderBottom } = useStickyOffset<HTMLDivElement>();
  // 점검완료 배너 하단 위치 → 사진 카드 sticky top
  const { ref: completionBannerRef, stickyTop: completionBannerBottom } = useStickyOffset<HTMLDivElement>();
  // 사진 카드 리스트 너비 → 2열 레이아웃 결정
  const photoListRef = useRef<HTMLDivElement>(null);
  const [useHorizontalLayout, setUseHorizontalLayout] = useState(false);

  // 섹션 접기/펼치기
  const [hazardSectionExpanded, setHazardSectionExpanded] = useState(true);
  const [bpSectionExpanded, setBpSectionExpanded] = useState(true);

  // photoListRef 너비 측정 — 탭 전환 또는 최초 사진 추가 시 재측정
  // hazardPhotos.length가 0→1로 바뀌면 photoListRef가 새 DOM 요소에 연결되므로 재등록 필요
  useEffect(() => {
    const el = photoListRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setUseHorizontalLayout(entry.contentRect.width >= 768);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [activeInspectionTab, hazardPhotos.length]);

  const canModifyNotLocked = (photo: Photo) => canModifyPhoto(photo) && !hasReport && !isCompletionLocked && !isLockedByLaterInspection;
  // 발견사항 해결은 이후 차수 잠금과 무관하게 가능
  const canResolveHazard = (photo: Photo) => canModifyPhoto(photo) && !hasReport && !isCompletionLocked;

  return (
    <section style={{ display: visible ? '' : 'none' }}>
      {/* 툴바 (Sticky - 탭 바 또는 서브헤더 아래 고정).
          시안 `.insp-toolbar`는 카드지만, 이 화면의 툴바는 스크롤 중에도 붙어 있어야 해
          (사진 카드 sticky 오프셋 체인의 출발점) 바깥에 full-bleed sticky 껍질을 두고
          그 안에 시안 카드를 넣는다. */}
      <div ref={sectionHeaderRef} className="sticky z-[24] -mx-4 bg-surface px-4 pb-3.5 pt-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8" style={{ top: stickyTop }}>
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[14px] border border-line bg-white px-[18px] py-[13px] shadow-card-sm">
          <div className="flex flex-wrap items-center gap-[18px]">
            {/* 위험영역 표시 공통 토글 */}
            <label className={CHECK_LABEL_CLASS}>
              <input
                type="checkbox"
                checked={showHazardOverlay}
                onChange={(e) => onShowHazardOverlayChange(e.target.checked)}
                className={CHECKBOX_CLASS}
              />
              위험영역 표시
            </label>

            {/* 그리기 메모 표시 토글 */}
            <label className={CHECK_LABEL_CLASS}>
              <input
                type="checkbox"
                checked={showAnnotationOverlay}
                onChange={(e) => onShowAnnotationOverlayChange(e.target.checked)}
                className={CHECKBOX_CLASS}
              />
              그리기 메모
            </label>

            {/*
              위험성평가 config 선택 드롭다운 — 완료 점검에서는 읽기 전용(방식 잠금).
              게이트가 canEdit을 보면 잠금 축(canEditRiskConfig)과 어긋나, 완료 점검을 여는
              편집 권한자에게 configs 로딩이 끝나기 전까지(그리고 활성 config가 0건이면 계속)
              드롭다운이 통째로 사라진다 — "숨기지 말고 읽기 전용으로" 규약 위반.
            */}
            {shouldShowRiskConfigDropdown(allRiskConfigs.length, canEditRiskConfigEffective) && onRiskConfigChange && (
              <RiskConfigDropdown
                inspectionId={inspectionId}
                currentConfigId={riskAssessmentConfigId}
                effectiveConfig={riskConfig}
                configs={allRiskConfigs}
                canEdit={canEditRiskConfigEffective}
                onSave={onRiskConfigChange}
                appearance="pill"
              />
            )}
          </div>

          <div className="flex items-center gap-2">
          {/* 사진 추가 버튼 */}
          {canEdit && (
            <>
              {onOpenCamera && (
                <Button
                  variant="secondary"
                  onClick={onOpenCamera}
                  disabled={isLoading || hasReport || isCompletionLocked}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  카메라 촬영
                </Button>
              )}
              {(hasReport || isCompletionLocked) ? (
                <PortalTooltip
                  content={hasReport ? '보고서가 생성되어 사진을 추가할 수 없습니다' : '점검완료 서명 후에는 사진을 추가할 수 없습니다'}
                  placement="top"
                  align="right"
                >
                  <div>
                    <Button
                      variant="primary"
                      onClick={onAddPhoto}
                      disabled={isLoading || hasReport || isCompletionLocked}
                    >
                      사진 추가
                    </Button>
                  </div>
                </PortalTooltip>
              ) : (
                <Button
                  variant="primary"
                  onClick={onAddPhoto}
                  disabled={isLoading || hasReport || isCompletionLocked}
                >
                  사진 추가
                </Button>
              )}
              {bestPracticeEnabled && onAddBestPracticePhoto && (
                (hasReport || isCompletionLocked) ? (
                  <PortalTooltip
                    content={hasReport ? '보고서가 생성되어 사진을 추가할 수 없습니다' : '점검완료 서명 후에는 사진을 추가할 수 없습니다'}
                    placement="top"
                    align="right"
                  >
                    <div>
                      <button
                        type="button"
                        className={BEST_PRACTICE_BUTTON_CLASS}
                        onClick={onAddBestPracticePhoto}
                        disabled={isLoading || hasReport || isCompletionLocked}
                        title="우수사례 사진 추가"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                        </svg>
                        우수사례
                      </button>
                    </div>
                  </PortalTooltip>
                ) : (
                  <button
                    type="button"
                    className={BEST_PRACTICE_BUTTON_CLASS}
                    onClick={onAddBestPracticePhoto}
                    disabled={isLoading || hasReport || isCompletionLocked}
                    title="우수사례 사진 추가"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                    </svg>
                    우수사례
                  </button>
                )
              )}
            </>
          )}
          </div>
        </div>
      </div>

      {/* 점검완료 잠금 안내 배너 (sticky) — 배너 없어도 sticky+top 유지하여 useStickyOffset 측정이 sectionHeaderBottom을 반환하도록 */}
      <div ref={completionBannerRef} className={`sticky z-[23] ${completion?.isMyCompleted ? '-mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8' : ''}`} style={{ top: sectionHeaderBottom }}>
        {completion?.isMyCompleted && !hasReport && (
          <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-b-lg text-sm text-green-700 shadow-sm">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>점검완료 서명이 완료되었습니다. 수정하려면 점검완료를 취소해주세요.</span>
          </div>
        )}
      </div>

      {/* 사진 카드 리스트 */}
      {hazardPhotos.length === 0 && bestPracticePhotos.length === 0 ? (
        <PhotoEmptyState
          title="아직 사진이 없습니다"
          description={
            hasReport
              ? '보고서가 생성되어 사진을 추가할 수 없습니다'
              : '현장 사진을 추가해 AI 위험요인 분석을 시작해보세요'
          }
          action={
            !hasReport && canEdit ? (
              <button
                type="button"
                onClick={onAddPhoto}
                disabled={isCompletionLocked}
                className={FIRST_PHOTO_BUTTON_CLASS}
              >
                <PlusIcon className="h-[17px] w-[17px]" />
                첫 사진 추가하기
              </button>
            ) : null
          }
        />
      ) : (
        <>
          {/* 위험요인 사진 섹션 */}
          {hazardPhotos.length > 0 && (
            <div>
              {bestPracticePhotos.length > 0 && (
                <button
                  type="button"
                  onClick={() => setHazardSectionExpanded(!hazardSectionExpanded)}
                  className="flex items-center gap-2 mt-3 mb-2 px-1 group"
                >
                  <span className="text-sm font-semibold text-ink-700">위험요인</span>
                  <span className="text-xs text-ink-500">({hazardPhotos.length}건)</span>
                  <svg className={`w-4 h-4 text-ink-400 transition-transform ${hazardSectionExpanded ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
              {hazardSectionExpanded && (
              <div className="space-y-4" ref={photoListRef}>
                {hazardPhotos.map((photo, idx) => {
                  const findingStartIndex = 1;
                  const canModify = canModifyNotLocked(photo);
                  const canResolve = canResolveHazard(photo);
                  const isPendingReview = !findingSelectionEnabled && !photo.isReviewed &&
                    photo.analysisStatus === 'COMPLETED' &&
                    canReviewPhoto(photo);
                  const globalIdx = allPhotos.findIndex(p => p.id === photo.id);

                  return (
                    <div
                      key={photo.id}
                      className="relative"
                      data-photo-id={photo.id}
                      data-pending-review={isPendingReview ? 'true' : undefined}
                    >
                      <PhotoCardVertical
                        photo={photo}
                        findings={photo.findings}
                        index={idx + 1}
                        findingStartIndex={findingStartIndex}
                        focusCategories={focusCategories}
                        isReviewed={photo.isReviewed}
                        showHazardOverlay={showHazardOverlay}
                        horizontalLayout={useHorizontalLayout}
                        stickyTopOffset={completionBannerBottom}
                        onReviewToggle={
                          !findingSelectionEnabled && canReviewPhoto(photo)
                            ? (checked) => onPhotoReview(photo.id, checked)
                            : undefined
                        }
                        reviewDisabled={!findingSelectionEnabled && !canReviewPhoto(photo) && canEdit}
                        onPhotoClick={!hasReport ? () => openPhotoViewer(allPhotos, globalIdx) : undefined}
                        selectable={canReviewPhoto(photo) && !isCompletionLocked}
                        isSelected={selectedPhotoIds.has(photo.id)}
                        onSelectionChange={(checked) => onSelectionChange(photo.id, checked)}
                        showAddedBy={contributorCount > 1}
                        highlightedFindingId={highlightedFindingId ?? undefined}
                        onFindingClick={(id) => onHighlightedFindingIdChange(id === highlightedFindingId ? null : id)}
                        editable={canEditFinding}
                        editHandlers={canEditFinding ? findingEditHandlers : undefined}
                        hazardEdits={Array.from(hazardEdits.values())}
                        onHazardChange={canEditHazard ? onHazardChange : undefined}
                        onHazardChangeEnd={canEditHazard ? onHazardChangeEnd : undefined}
                        onHazardDeleteSave={canEditHazard ? onHazardDeleteSave : undefined}
                        onHazardRestore={canEditHazard ? onHazardRestore : undefined}
                        showAnnotationOverlay={showAnnotationOverlay}
                        companyId={companyId}
                        siteId={siteId}
                        inspectionId={inspectionId}
                        onResolveHazard={
                          hazardResolutionEnabled && canResolve
                            ? (findingId?: string) => onOpenHazardResolution?.(photo.id, findingId)
                            : undefined
                        }
                        hasReport={hasReport}
                        isCompletionLocked={isCompletionLocked}
                        hazardResolutionEnabled={hazardResolutionEnabled}
                        onPhotoMetaUpdate={onPhotoMetaUpdate}
                        onAddFinding={
                          canModify && hasAddFinding
                            ? () => onOpenAddFindingSheet(photo.id)
                            : undefined
                        }
                        findingSelectionEnabled={findingSelectionEnabled}
                        onToggleMeasureSelection={isCompletionLocked ? undefined : onToggleMeasureSelection}
                        currentUserId={currentUserId}
                        isMyCompleted={completion?.isMyCompleted ?? false}
                        isPrimary={isPrimary}
                        isOwner={isOwner}
                        riskConfig={riskConfig}
                        appearance="aligned"
                        headerAction={
                          <PhotoDropdownMenu
                            photoId={photo.id}
                            hasFindings={photo.findings.length > 0}
                            onViewFullsize={() => openPhotoViewer(allPhotos, globalIdx)}
                            onDownload={() => onPhotoDownload(photo.id)}
                            onAddFinding={
                              canModify && hasAddFinding
                                ? () => onOpenAddFindingSheet(photo.id)
                                : undefined
                            }
                            onResolveHazard={
                              hazardResolutionEnabled && canResolve && photo.findings.filter(f => !f.deletedAt).length > 0
                                ? () => {
                                    const activeFindings = photo.findings.filter(f => !f.deletedAt);
                                    const allResolved = activeFindings.every(f => f.resolutionStatus === 'COMPLIED');
                                    if (!allResolved) {
                                      onOpenHazardResolution?.(photo.id);
                                    }
                                  }
                                : undefined
                            }
                            resolveHazardDisabled={
                              hazardResolutionEnabled && canResolve && photo.findings.filter(f => !f.deletedAt).length > 0
                                ? photo.findings.filter(f => !f.deletedAt).every(f => f.resolutionStatus === 'COMPLIED')
                                : undefined
                            }
                            resolveHazardDisabledReason="모든 위험요인이 해소되었습니다"
                            onReanalyze={canModify ? () => onPhotoReanalyze(photo.id) : undefined}
                            onReanalyzeWithRegion={canModify ? () => onPhotoReanalyzeWithRegion(photo.id) : undefined}
                            onDelete={canModify ? () => onPhotoDelete(photo.id) : undefined}
                          />
                        }
                      />
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          )}

          {/* 우수사례 사진 섹션 */}
          {bestPracticeEnabled && bestPracticePhotos.length > 0 && (
            <div className="mt-6">
              <button
                type="button"
                onClick={() => setBpSectionExpanded(!bpSectionExpanded)}
                className="flex items-center gap-2 mb-2 px-1 group"
              >
                <svg className="w-4 h-4 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                </svg>
                <span className="text-sm font-semibold text-emerald-700">우수사례</span>
                <span className="text-xs text-emerald-600">({bestPracticePhotos.length}건)</span>
                <svg className={`w-4 h-4 text-emerald-400 transition-transform ${bpSectionExpanded ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {bpSectionExpanded && (
              <div className="space-y-4">
                {bestPracticePhotos.map((photo, idx) => {
                  const globalIdx = allPhotos.findIndex(p => p.id === photo.id);
                  const canModify = canModifyPhoto(photo);

                  return (
                    <div
                      key={photo.id}
                      className="relative"
                      data-photo-id={photo.id}
                    >
                      <PhotoCardVertical
                        photo={photo}
                        findings={photo.findings}
                        index={idx + 1}
                        findingStartIndex={1}
                        isReviewed={photo.isReviewed}
                        showHazardOverlay={false}
                        horizontalLayout={useHorizontalLayout}
                        stickyTopOffset={completionBannerBottom}
                        onReviewToggle={
                          !findingSelectionEnabled && canReviewPhoto(photo)
                            ? (checked) => onPhotoReview(photo.id, checked)
                            : undefined
                        }
                        reviewDisabled={!findingSelectionEnabled && !canReviewPhoto(photo) && canEdit}
                        onPhotoClick={!hasReport ? () => openPhotoViewer(allPhotos, globalIdx) : undefined}
                        selectable={canReviewPhoto(photo) && !isCompletionLocked}
                        isSelected={selectedPhotoIds.has(photo.id)}
                        onSelectionChange={(checked) => onSelectionChange(photo.id, checked)}
                        showAddedBy={contributorCount > 1}
                        editable={canEditFinding}
                        editHandlers={canEditFinding ? findingEditHandlers : undefined}
                        showAnnotationOverlay={showAnnotationOverlay}
                        companyId={companyId}
                        siteId={siteId}
                        inspectionId={inspectionId}
                        hasReport={hasReport}
                        hazardResolutionEnabled={false}
                        onAddFinding={
                          canModify && !hasReport && hasAddFinding
                            ? () => onOpenAddFindingSheet(photo.id, 'best-practice')
                            : undefined
                        }
                        findingSelectionEnabled={findingSelectionEnabled}
                        onToggleMeasureSelection={isCompletionLocked ? undefined : onToggleMeasureSelection}
                        currentUserId={currentUserId}
                        isMyCompleted={completion?.isMyCompleted ?? false}
                        isPrimary={isPrimary}
                        isOwner={isOwner}
                        riskConfig={riskConfig}
                        appearance="aligned"
                        headerAction={
                          <PhotoDropdownMenu
                            photoId={photo.id}
                            hasFindings={photo.findings.length > 0}
                            onViewFullsize={() => openPhotoViewer(allPhotos, globalIdx)}
                            onDownload={() => onPhotoDownload(photo.id)}
                            onAddFinding={
                              canModify && !hasReport && hasAddFinding
                                ? () => onOpenAddFindingSheet(photo.id, 'best-practice')
                                : undefined
                            }
                            onReanalyze={canModify && !hasReport ? () => onPhotoReanalyze(photo.id) : undefined}
                            onReanalyzeWithRegion={canModify && !hasReport ? () => onPhotoReanalyzeWithRegion(photo.id) : undefined}
                            onDelete={canModify && !hasReport ? () => onPhotoDelete(photo.id) : undefined}
                          />
                        }
                      />
                    </div>
                  );
                })}
              </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}
